import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { CurrentUserResponse, LoginRequest, LoginResponse, User } from '../models/api.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private router = inject(Router);
  private readonly apiUrl = `${environment.apiUrl}/auth`;
  private sessionChecked = false;
  private sessionExpired = false;
  private logoutPromise: Promise<void> | null = null;

  readonly user = signal<User | null>(null);
  readonly isLoggedIn = computed(() => this.user() !== null);

  async login(credentials: LoginRequest): Promise<User> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>(`${this.apiUrl}/login`, credentials, {
        withCredentials: true,
      }),
    );

    this.user.set(response.user);

    this.sessionChecked = true;
    this.sessionExpired = false;

    return response.user;
  }

  async restoreSession(checkSessionExpired = false): Promise<boolean> {
    if (this.user() && !checkSessionExpired) return true;
    if (this.sessionChecked && !checkSessionExpired) return false;

    const hadUser = this.user() !== null;

    this.sessionChecked = true;

    try {
      const response = await firstValueFrom(
        this.http.get<CurrentUserResponse>(`${this.apiUrl}/me`, {
          withCredentials: true,
        }),
      );
      this.user.set(response.user);
      return true;
    } catch (error: unknown) {
      const backendMessage =
        error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
          ? error.error.message
          : '';

      this.sessionExpired =
        hadUser ||
        (error instanceof HttpErrorResponse && error.status === 401 &&
          (backendMessage.includes('หมดอายุ') || backendMessage.includes('ไม่ถูกต้อง')));
      this.user.set(null);
      return false;
    }
  }

  consumeSessionExpired(): boolean {
    const expired = this.sessionExpired;
    this.sessionExpired = false;
    return expired;
  }

  logout(): Promise<void> {
    if (this.logoutPromise) return this.logoutPromise;

    this.logoutPromise = this.performLogout();
    return this.logoutPromise;
  }

  private async performLogout(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/logout`, {}, { withCredentials: true }));
    } catch {
      // ล้างสถานะฝั่ง Frontend ต่อ แม้ Backend ติดต่อไม่ได้
    } finally {
      this.user.set(null);
      this.sessionChecked = true;
      this.sessionExpired = false;
      await this.router.navigateByUrl('/login');
      this.logoutPromise = null;
    }
  }
}
